import { defineStore } from 'pinia';
import { ref } from 'vue';

export type DialogType = 'info' | 'warning' | 'error' | 'success' | 'question';

export interface DialogOptions {
  title: string;
  message: string;
  type?: DialogType;
  confirmText?: string;
  cancelText?: string;
  persistent?: boolean;
  width?: string | number;
  timeout?: number; // 타임아웃 (밀리초, 0이면 자동 닫힘 없음)
}

export const useDialogStore = defineStore('dialog', () => {
  const show = ref(false);
  const title = ref('');
  const message = ref('');
  const type = ref<DialogType>('info');
  const confirmText = ref('OK');
  const cancelText = ref('Cancel');
  const persistent = ref(false);
  const width = ref<string | number>('600px'); // 기본 너비를 600px로 증가
  const timeout = ref<number>(0); // 0이면 타임아웃 없음
  
  let resolveCallback: ((value: boolean) => void) | null = null;
  let timeoutTimer: number | null = null;
  type DialogQueueItem = {
    options: DialogOptions;
    resolve: (value: boolean) => void;
  };
  const dialogQueue: DialogQueueItem[] = [];
  let isShowingDialog = false;

  const clearTimer = () => {
    if (timeoutTimer) {
      window.clearTimeout(timeoutTimer);
      timeoutTimer = null;
    }
  };

  const applyOptions = (options: DialogOptions) => {
    title.value = options.title;
    message.value = options.message;
    type.value = options.type || 'info';
    confirmText.value = options.confirmText || 'OK';
    cancelText.value = options.cancelText || ''; // 기본값은 빈 문자열 (취소 버튼 없음)
    persistent.value = options.persistent || false;
    width.value = options.width || '600px'; // 기본 너비를 600px로 증가
    timeout.value = options.timeout || 0;
    show.value = true;
  };

  const showNextDialog = () => {
    // isShowingDialog=true 인데 show=false 인 "stuck" 상태 복구
    if (isShowingDialog) {
      if (show.value) return;

      console.warn('[DialogStore] stuck dialog detected. Recovering...', {
        queueLength: dialogQueue.length,
        title: title.value,
        message: message.value,
        type: type.value,
      });

      try {
        if (resolveCallback) resolveCallback(false);
      } catch {
        // noop: resolveCallback 정리 실패는 무시
      }

      resolveCallback = null;
      isShowingDialog = false;
      clearTimer();
    }

    if (isShowingDialog) return;
    if (dialogQueue.length === 0) return;

    clearTimer();

    const next = dialogQueue.shift();
    if (!next) return;

    isShowingDialog = true;
    resolveCallback = next.resolve;
    applyOptions(next.options);

    // 타임아웃이 설정되어 있으면 자동 닫기
    if (timeout.value > 0) {
      timeoutTimer = window.setTimeout(() => {
        close();
      }, timeout.value);
    }
  };

  const showDialog = (options: DialogOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      dialogQueue.push({ options, resolve });
      showNextDialog();
    });
  };

  const confirm = () => {
    clearTimer();
    if (resolveCallback) {
      resolveCallback(true);
      resolveCallback = null;
    }
    show.value = false;
    isShowingDialog = false;
    showNextDialog();
  };

  const cancel = () => {
    clearTimer();
    if (resolveCallback) {
      resolveCallback(false);
      resolveCallback = null;
    }
    show.value = false;
    isShowingDialog = false;
    showNextDialog();
  };

  const close = () => {
    clearTimer();
    if (!persistent.value) {
      if (resolveCallback) {
        resolveCallback(false);
        resolveCallback = null;
      }
      show.value = false;
      isShowingDialog = false;
      showNextDialog();
    }
  };

  /**
   * persistent 다이얼로그도 X 버튼으로 강제 닫을 수 있게 하는 함수
   * - 바깥 클릭(@click:outside)은 persistent면 닫지 않도록 유지
   * - X 버튼은 무조건 닫힘
   */
  const forceClose = () => {
    clearTimer();

    if (resolveCallback) {
      resolveCallback(false);
      resolveCallback = null;
    }

    show.value = false;
    isShowingDialog = false;
    persistent.value = false;

    showNextDialog();
  };

  // 예외 상황(HMR/비정상 중단)에서 overlay/대기 Promise 잔존을 강제로 정리
  const resetDialogState = () => {
    clearTimer();
    if (resolveCallback) {
      resolveCallback(false);
      resolveCallback = null;
    }
    while (dialogQueue.length > 0) {
      const item = dialogQueue.shift();
      if (item) item.resolve(false);
    }
    show.value = false;
    isShowingDialog = false;
    persistent.value = false;
  };

  return {
    show,
    title,
    message,
    type,
    confirmText,
    cancelText,
    persistent,
    width,
    timeout,
    showDialog,
    confirm,
    cancel,
    close,
    forceClose,
    resetDialogState,
  };
});

